"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatMessage } from "../MessageBubble";
import { readSeen, writeSeen } from "./seenMessages";

/**
 * An unchanged merged batch should keep the same state reference; otherwise
 * an empty poll would rerun scrolling even though no new message arrived.
 */
function sameMessages(a: ChatMessage[], b: ChatMessage[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (m, i) => m.id === b[i].id && m.content === b[i].content
  );
}

/**
 * The thread's messages, the one write path every arrival goes through, and
 * the unread bookkeeping that path maintains. Also holds the flags the other
 * chatThread hooks consult on every turn — whether the component is still
 * mounted, whether a send is in flight, whether the reader is following the
 * bottom — since each of them is read and written from more than one place.
 */
export function useMessageThread({
  initialMessages,
  seenKey,
  visible,
}: {
  initialMessages: ChatMessage[];
  /** Where this thread remembers the reader's last-read message. */
  seenKey: string;
  visible: boolean;
}) {
  const [msgs, setMsgs] = useState<ChatMessage[]>(initialMessages);
  /** Something arrived below the fold while the reader was scrolled up. */
  const [hasUnread, setHasUnread] = useState(false);
  /**
   * First message the reader has not seen: a reply that arrived on its own
   * (a rebuild finishing, a run reporting) rather than in answer to what
   * they just typed. The line stays until they send something.
   */
  const [unreadFromId, setUnreadFromId] = useState<string | null>(null);
  const mountedRef = useRef(false);
  /** False until the first server sync, which is compared against the remembered last-read id. */
  const primedRef = useRef(false);
  /**
   * Mirrors `msgs` so incoming batches can be diffed outside of React state.
   * Detecting "new content arrived" in the async fetch (rather than in an
   * effect watching `msgs`) keeps setState out of effect bodies.
   */
  const msgsRef = useRef<ChatMessage[]>(initialMessages);
  const sendingRef = useRef(false);
  /** False while the user has scrolled up to read history — don't pull them back. */
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /** Single write path for the thread: diffs, stores, and flags unread. */
  const commitMessages = useCallback((next: ChatMessage[], notify = true) => {
    const prev = msgsRef.current;
    const previousIds = new Set(prev.map(message => message.id));
    const grew = next.some(message => !previousIds.has(message.id));
    // Replies to what the reader just typed are not "unread": only what
    // arrived by itself, or since they were last here, gets the line.
    if (notify && !primedRef.current) {
      primedRef.current = true;
      const seen = readSeen(seenKey);
      const at = seen ? next.findIndex((m) => m.id === seen) : -1;
      if (seen) {
        const first = next.slice(at + 1).find((m) => m.role === "assistant");
        if (first) setUnreadFromId(first.id);
      }
    } else if (notify && !sendingRef.current) {
      const known = new Set(prev.map((m) => m.id));
      const first = next.find((m) => !known.has(m.id) && m.role === "assistant");
      if (first) setUnreadFromId((cur) => cur ?? first.id);
    }
    if (sameMessages(msgsRef.current, next)) return;
    msgsRef.current = next;
    setMsgs(next);
    if (notify && grew && !stickToBottomRef.current) setHasUnread(true);
  }, [seenKey]);

  /** Reading the bottom of the thread while the tab is visible counts as having seen it. */
  const markRead = useCallback(() => {
    const last = msgsRef.current[msgsRef.current.length - 1];
    if (!last || last.id.startsWith("local-")) return;
    if (!visible || document.visibilityState !== "visible" || !stickToBottomRef.current) return;
    writeSeen(seenKey, last.id);
  }, [seenKey, visible]);

  useEffect(() => {
    markRead();
    document.addEventListener("visibilitychange", markRead);
    return () => document.removeEventListener("visibilitychange", markRead);
  }, [msgs, markRead]);

  return {
    msgs,
    setMsgs,
    msgsRef,
    mountedRef,
    sendingRef,
    stickToBottomRef,
    hasUnread,
    setHasUnread,
    unreadFromId,
    setUnreadFromId,
    commitMessages,
    markRead,
  };
}

export type MessageThread = ReturnType<typeof useMessageThread>;
